<template>
    <div class="consumption">
        <VHeader page="Consumption Stats"></VHeader>

        <div class="consumption-filters">
            <div class="consumption-filters__field">
                <label>Begin</label>
                <input type="date" v-model="filters.begin" />
            </div>
            <div class="consumption-filters__field">
                <label>End</label>
                <input type="date" v-model="filters.end" />
            </div>
            <div class="consumption-filters__field">
                <label>Bundle ID</label>
                <VInput v-model="filters.bundleId" placeholder="com.example.app"></VInput>
            </div>
            <VButton theme="dark-blue" @click="load">Load</VButton>
        </div>

        <template v-if="loaded">
            <div class="consumption-cards">
                <div class="consumption-card">
                    <div class="consumption-card__label">Total Requests / Uniq</div>
                    <div class="consumption-card__value">{{ summary.total }} / {{ summary.uniq_total }}</div>
                </div>
                <div class="consumption-card">
                    <div class="consumption-card__label">Won / Uniq</div>
                    <div class="consumption-card__value">{{ summary.won }} / {{ summary.uniq_won }}</div>
                </div>
                <div class="consumption-card">
                    <div class="consumption-card__label">Won % / Uniq</div>
                    <div class="consumption-card__value">{{ summary.won_pct }}% / {{ uniqWonPct(summary) }}%</div>
                </div>
            </div>

            <div class="consumption-table">
                <div class="consumption-table-header">
                    <div
                        class="consumption-table-header-title consumption-table-header-title__bundle consumption-table-header-title__sortable"
                        @click="toggleSort('bundle_id')"
                    >
                        Bundle ID {{ sortIndicator('bundle_id') }}
                    </div>
                    <div
                        class="consumption-table-header-title consumption-table-header-title__num consumption-table-header-title__sortable"
                        @click="toggleSort('total')"
                    >
                        Requests / Uniq {{ sortIndicator('total') }}
                    </div>
                    <div
                        class="consumption-table-header-title consumption-table-header-title__num consumption-table-header-title__sortable"
                        @click="toggleSort('won')"
                    >
                        Won / Uniq {{ sortIndicator('won') }}
                    </div>
                    <div
                        class="consumption-table-header-title consumption-table-header-title__num consumption-table-header-title__sortable"
                        @click="toggleSort('won_pct')"
                    >
                        Won % / Uniq {{ sortIndicator('won_pct') }}
                    </div>
                </div>

                <div class="consumption-table-row" v-for="row in rows" :key="row.bundle_id">
                    <div class="consumption-table-row-text consumption-table-row-text__bundle">{{ row.bundle_id }}</div>
                    <div class="consumption-table-row-text consumption-table-row-text__num">{{ row.total }} / {{ row.uniq_total }}</div>
                    <div class="consumption-table-row-text consumption-table-row-text__num">{{ row.won }} / {{ row.uniq_won }}</div>
                    <div class="consumption-table-row-text consumption-table-row-text__num">{{ row.won_pct }}% / {{ uniqWonPct(row) }}%</div>
                </div>

                <div class="consumption-table-footer">
                    <div class="consumption-table-footer-text consumption-table-footer-text__bundle">Total</div>
                    <div class="consumption-table-footer-text consumption-table-footer-text__num">{{ summary.total }} / {{ summary.uniq_total }}</div>
                    <div class="consumption-table-footer-text consumption-table-footer-text__num">{{ summary.won }} / {{ summary.uniq_won }}</div>
                    <div class="consumption-table-footer-text consumption-table-footer-text__num">{{ summary.won_pct }}% / {{ uniqWonPct(summary) }}%</div>
                </div>
            </div>

            <div class="consumption-pagination">
                <VButton :disabled="page <= 1" @click="prevPage">Prev</VButton>
                <span class="consumption-pagination__info">
                    Showing {{ totalCount }} bundles &mdash; Page {{ page }} of {{ pagesCount }}
                </span>
                <VButton :disabled="page >= pagesCount" @click="nextPage">Next</VButton>
            </div>
        </template>

        <template v-if="isLoading">
            <VLoadingRow height="200px"></VLoadingRow>
        </template>
    </div>
</template>

<script>
    import VButton from '../components/VButton';
    import VHeader from '../components/VHeader';
    import VInput from '../components/VInput';
    import VLoadingRow from '../components/VLoadingRow';
    import apiConsumption from '../api/apiConsumption';

    export default {
        name: 'ConsumptionStats',
        components: {
            VButton,
            VHeader,
            VInput,
            VLoadingRow,
        },
        data() {
            const now = new Date();
            const end = now.toISOString().slice(0, 10);
            const beginDate = new Date(now);
            beginDate.setMonth(beginDate.getMonth() - 1);
            const begin = beginDate.toISOString().slice(0, 10);

            return {
                filters: {
                    begin,
                    end,
                    bundleId: '',
                },
                sort: 'won_pct',
                order: 'desc',
                page: 1,
                perPage: 20,
                pagesCount: 0,
                totalCount: 0,
                rows: [],
                summary: {},
                isLoading: false,
                loaded: false,
            };
        },
        created() {
            this.load();
        },
        methods: {
            load() {
                this.isLoading = true;
                apiConsumption.getStats({
                    begin: this.filters.begin,
                    end: this.filters.end,
                    bundleId: this.filters.bundleId,
                    sort: this.sort,
                    order: this.order,
                    page: this.page,
                    perPage: this.perPage,
                }).then((res) => {
                    const data = res.data;
                    this.rows = data.rows;
                    this.summary = data.summary;
                    this.pagesCount = data.pages_count;
                    this.totalCount = data.total_count;
                    this.page = data.page;
                    this.loaded = true;
                }).catch(() => {
                    this.$toastr.longError('Failed to load consumption stats');
                }).finally(() => {
                    this.isLoading = false;
                });
            },
            toggleSort(col) {
                if (this.sort === col) {
                    this.order = this.order === 'asc' ? 'desc' : 'asc';
                } else {
                    this.sort = col;
                    this.order = 'desc';
                }
                this.page = 1;
                this.load();
            },
            sortIndicator(col) {
                if (this.sort !== col) return '';
                return this.order === 'asc' ? '\u25B2' : '\u25BC';
            },
            // uniqWonPct is the won rate over deduped requests (uniq_won/uniq_total),
            // independent of the backend's row-based won_pct. Rounded to 2 decimals,
            // guards division by zero.
            uniqWonPct(o) {
                if (!o || !o.uniq_total) return 0;
                return Math.round((o.uniq_won / o.uniq_total) * 10000) / 100;
            },
            prevPage() {
                if (this.page > 1) {
                    this.page--;
                    this.load();
                }
            },
            nextPage() {
                if (this.page < this.pagesCount) {
                    this.page++;
                    this.load();
                }
            },
        },
    };
</script>

<style scoped lang="scss">
    .consumption {
        padding: 0 24px 24px;

        &-filters {
            display: flex;
            align-items: flex-end;
            gap: 16px;
            margin-bottom: 24px;
            flex-wrap: wrap;

            &__field {
                display: flex;
                flex-direction: column;
                gap: 4px;

                label {
                    font-size: 12px;
                    color: rgba($dark-blue-color, 0.6);
                    font-weight: 500;
                    font-family: Quicksand;
                }

                input[type="date"] {
                    height: 28px;
                    padding: 0 8px;
                    border: 1px solid $primary-color;
                    border-radius: 4px;
                    font-size: 13px;
                    font-family: Quicksand;
                    color: $dark-blue-color;
                    outline: none;

                    &:focus {
                        @include blue-shadow;
                    }
                }
            }
        }

        &-cards {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
            margin-bottom: 24px;
        }

        &-card {
            background: #fff;
            border-radius: 10px;
            padding: 16px;
            @include light-shadow;

            &__label {
                font-size: 12px;
                color: rgba($dark-blue-color, 0.6);
                text-transform: uppercase;
                letter-spacing: 0.05em;
                margin-bottom: 6px;
                font-family: Quicksand;
            }

            &__value {
                font-size: 24px;
                font-weight: 700;
                color: $dark-blue-color;
                font-family: Poppins;
            }
        }

        &-table {
            border-radius: 10px;
            @include dark-shadow;
            margin-bottom: 16px;
            overflow: hidden;

            &-header {
                display: flex;
                height: 45px;
                border-bottom: 1px solid $primary-color;

                &-title {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: $dark-blue-color;
                    font-size: 12px;
                    font-weight: 600;
                    font-family: Poppins;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;

                    &__bundle {
                        flex: 2;
                        justify-content: flex-start;
                        padding-left: 16px;
                    }

                    &__num {
                        text-align: right;
                        justify-content: flex-end;
                        padding-right: 16px;
                    }

                    &__sortable {
                        cursor: pointer;
                        user-select: none;

                        &:hover {
                            color: $primary-color;
                        }
                    }
                }
            }

            &-row {
                display: flex;
                align-items: center;
                min-height: 45px;
                border-top: 1px solid rgba($primary-color, 0.2);

                &:hover {
                    background: rgba($light-blue-color, 0.4);
                }

                &-text {
                    flex: 1;
                    padding: 10px 16px;
                    text-align: center;
                    color: $dark-blue-color;
                    font-size: 14px;
                    font-family: Quicksand;
                    word-break: break-word;

                    &__bundle {
                        flex: 2;
                        text-align: left;
                        font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
                        font-size: 13px;
                    }

                    &__num {
                        text-align: right;
                        font-variant-numeric: tabular-nums;
                    }
                }
            }

            &-footer {
                display: flex;
                align-items: center;
                min-height: 45px;
                border-top: 2px solid rgba($primary-color, 0.3);
                background: rgba($light-blue-color, 0.3);

                &-text {
                    flex: 1;
                    padding: 10px 16px;
                    text-align: center;
                    color: $dark-blue-color;
                    font-size: 14px;
                    font-weight: 700;
                    font-family: Poppins;

                    &__bundle {
                        flex: 2;
                        text-align: left;
                    }

                    &__num {
                        text-align: right;
                        font-variant-numeric: tabular-nums;
                    }
                }
            }
        }

        &-pagination {
            display: flex;
            align-items: center;
            gap: 12px;
            justify-content: center;

            &__info {
                font-size: 14px;
                color: rgba($dark-blue-color, 0.6);
                font-family: Quicksand;
            }
        }
    }
</style>
